/**
 * Skills — a named, versioned recipe of tool calls the agent can run by name
 * or find on its own from a one-line `when_to_use` match (AGENT-DESIGN.md
 * §2.3, ticket BETA-31). Backed by core/intelligence/agentskills through
 * /api/agent/skills.
 *
 * `list_skills` and `save_skill` are static, like every other tool in this
 * directory. `run_skill` is NOT exported statically — its `name` schema must
 * enumerate the live, enabled skill slugs (Gemini's OpenAI-compat path
 * refuses an argument value the schema never advertised, AGENT-DESIGN.md
 * §1.6/§2.3), so `buildRunSkillTool(skillIndex)` compiles a fresh tool object
 * every turn from the index `turn.js` already fetches. See turn.js for where
 * it's spliced into the registry.
 */

import * as api from "../../api";

// recursionGuardedTools can never appear as a skill step — running run_skill
// or save_skill from inside a skill would let a self-referential skill loop
// forever; a skill is a saved plan, never a place to build new plans.
const RECURSION_GUARDED_TOOLS = new Set(["run_skill", "save_skill", "list_skills"]);

const TEMPLATE_PLACEHOLDER = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

// resolveTemplate substitutes {{param}} placeholders found in string leaves
// of tmpl with values from params. A placeholder that IS the whole string
// keeps the param's native type; one embedded in a larger string is
// stringified. A missing param is left as literal text.
function resolveValue(value, params) {
  if (typeof value === "string") {
    const whole = value.match(/^\{\{\s*([a-zA-Z0-9_]+)\s*\}\}$/);
    if (whole) {
      return whole[1] in params ? params[whole[1]] : value;
    }
    return value.replace(TEMPLATE_PLACEHOLDER, (m, key) =>
      key in params ? String(params[key]) : m
    );
  }
  if (Array.isArray(value)) return value.map((v) => resolveValue(v, params));
  if (value && typeof value === "object") return resolveTemplate(value, params);
  return value;
}

function resolveTemplate(tmpl, params) {
  const out = {};
  for (const [k, v] of Object.entries(tmpl || {})) out[k] = resolveValue(v, params || {});
  return out;
}

export const list_skills = {
  name: "list_skills",
  description:
    "List every saved skill — name, title, summary, when_to_use and whether it's enabled. A disabled " +
    "skill is a draft nobody has reviewed yet; run_skill refuses it. Use this to see what exists.",
  parameters: { type: "object", properties: {}, additionalProperties: false },
  execute: async () => {
    try {
      const { skills = [] } = await api.listAgentSkills();
      return {
        ok: true,
        data: {
          skills: skills.map((s) => ({
            name: s.name,
            title: s.title,
            summary: s.summary,
            whenToUse: s.whenToUse,
            version: s.currentVersion,
            enabled: s.enabled,
          })),
        },
      };
    } catch (err) {
      return { ok: false, error: { code: "LIST_SKILLS_FAILED", message: String(err?.message || err) } };
    }
  },
};

/**
 * buildRunSkillTool compiles run_skill fresh for this turn: the `name`
 * property's enum is the live, enabled skill slugs from the index turn.js
 * already fetched (see prefetch on the Go side for the same one-read,
 * two-uses shape).
 *
 * @param {{name: string, whenToUse: string}[]} skillIndex
 */
export function buildRunSkillTool(skillIndex = []) {
  const names = skillIndex.map((s) => s.name);
  const catalog = skillIndex
    .map((s) => (s.whenToUse ? `${s.name} — ${s.whenToUse}` : s.name))
    .join("; ");
  let description =
    "Run a saved skill by name — a versioned recipe of tool calls for a procedure the team already " +
    "documented. Each step still goes through the normal tool path, including request_confirmation: a " +
    "gated step still raises its approval card and still ends your turn exactly as if you had called it " +
    "directly. Pass params matching the skill's declared parameters; a skill with no params takes {}.";
  description += catalog ? ` Available skills: ${catalog}.` : " No skills are currently enabled.";

  const nameSchema = {
    type: "string",
    description: "The skill's name (slug), exactly as listed above or from list_skills.",
  };
  if (names.length > 0) nameSchema.enum = names;

  return {
    name: "run_skill",
    description,
    parameters: {
      type: "object",
      properties: {
        name: nameSchema,
        params: {
          type: "object",
          description:
            'Named arguments the skill\'s steps reference, e.g. {"id": "abc-123"}.',
        },
      },
      required: ["name"],
      additionalProperties: false,
    },
    // ctx.dispatch is the SAME registry's dispatch function this tool is
    // itself running inside of — turn.js attaches it to ctx right after
    // building the registry, so a step routes through the exact same gate
    // (confirm.js's GATED_KINDS) any top-level call would.
    execute: async ({ name, params = {} }, ctx) => {
      if (!ctx || typeof ctx.dispatch !== "function") {
        return {
          ok: false,
          error: { code: "RUN_SKILL_FAILED", message: "run_skill has no dispatcher in this context." },
        };
      }
      let skill;
      try {
        skill = await api.getAgentSkill(name);
      } catch (err) {
        if (err?.status === 404) {
          return {
            ok: false,
            error: {
              code: "SKILL_NOT_FOUND",
              message: `No skill named "${name}".`,
              hint: "Call list_skills to see what exists.",
            },
          };
        }
        return { ok: false, error: { code: "RUN_SKILL_FAILED", message: String(err?.message || err) } };
      }
      if (!skill.enabled) {
        return {
          ok: false,
          error: { code: "SKILL_DISABLED", message: `Skill "${name}" is disabled (a draft, or retired).` },
        };
      }
      const steps = skill.version?.steps || [];
      if (steps.length === 0) {
        return { ok: false, error: { code: "EMPTY_SKILL", message: `Skill "${name}" has no steps.` } };
      }

      const stepResults = [];
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        if (RECURSION_GUARDED_TOOLS.has(step.tool)) {
          return {
            ok: false,
            error: {
              code: "INVALID_SKILL_STEP",
              message: `Step ${i + 1} of "${name}" calls "${step.tool}", which a skill may not call.`,
            },
          };
        }
        const resolvedArgs = resolveTemplate(step.args_template, params);
        const result = await ctx.dispatch(step.tool, resolvedArgs);
        stepResults.push({ tool: step.tool, note: step.note, result });
        if (!result || result.ok !== true) {
          // Bubble the step's result verbatim when it's a pending-confirmation
          // card: agentRunner looks for `call.result.error.code ===
          // "PENDING_CONFIRMATION"` on run_skill's OWN result (the top-level
          // call), so the gated step raises its card exactly as if the model
          // had called request_confirmation itself.
          if (result?.error?.code === "PENDING_CONFIRMATION") {
            return result;
          }
          return {
            ok: false,
            error: {
              code: "SKILL_STEP_FAILED",
              message: `Skill "${name}" stopped at step ${i + 1} (${step.tool}): ${result?.error?.message || "failed"}`,
            },
            data: { skill: name, steps: stepResults },
          };
        }
      }
      return { ok: true, data: { skill: name, steps: stepResults } };
    },
  };
}

export const save_skill = {
  name: "save_skill",
  description:
    "Save a procedure as a reusable skill, from the tool calls you (or the user) just walked through in " +
    "this conversation. Saved as a DISABLED draft — a human reviews and enables it from the Skills page " +
    "before run_skill or the prompt index can use it, so say it was saved as a draft for review, not that " +
    "it's ready to use. If `name` already names an existing skill, this appends a new version instead of " +
    "failing.",
  parameters: {
    type: "object",
    properties: {
      name: {
        type: "string",
        description:
          'Slug for the skill: lowercase letters, digits and hyphens, e.g. "connect-modbus-client-from-datasheet".',
      },
      title: { type: "string" },
      summary: { type: "string" },
      when_to_use: {
        type: "string",
        description:
          "One line describing when this skill applies — this is what the prompt index and skill matching use.",
      },
      body_md: { type: "string" },
      steps: {
        type: "array",
        items: {
          type: "object",
          properties: {
            tool: { type: "string" },
            args_template: { type: "object" },
            note: { type: "string" },
          },
          required: ["tool"],
          additionalProperties: false,
        },
      },
      params: {
        type: "array",
        items: {
          type: "object",
          properties: {
            name: { type: "string" },
            type: { type: "string" },
            required: { type: "boolean" },
            description: { type: "string" },
          },
          required: ["name", "type"],
          additionalProperties: false,
        },
      },
    },
    required: ["name", "title", "when_to_use", "steps"],
    additionalProperties: false,
  },
  execute: async ({ name, title, summary, when_to_use, body_md, steps, params }) => {
    if (!Array.isArray(steps) || steps.length === 0) {
      return { ok: false, error: { code: "INVALID_ARGS", message: "steps must be a non-empty list." } };
    }
    for (const [i, step] of steps.entries()) {
      if (!step?.tool) {
        return { ok: false, error: { code: "INVALID_ARGS", message: `step ${i + 1} needs a tool name.` } };
      }
      if (RECURSION_GUARDED_TOOLS.has(step.tool)) {
        return {
          ok: false,
          error: {
            code: "INVALID_ARGS",
            message: `step ${i + 1} calls "${step.tool}", which a skill may not call.`,
          },
        };
      }
    }
    const payload = { name, title, summary, whenToUse: when_to_use, bodyMd: body_md, steps, params, enabled: false };
    try {
      const created = await api.createAgentSkill(payload);
      return { ok: true, data: { name: created.name, version: created.version?.version, status: "draft" } };
    } catch (err) {
      if (err?.status !== 409) {
        return { ok: false, error: { code: "SAVE_SKILL_FAILED", message: String(err?.message || err) } };
      }
    }
    try {
      const versioned = await api.newAgentSkillVersion(name, { bodyMd: body_md, steps, enabled: false });
      return { ok: true, data: { name: versioned.name, version: versioned.version?.version, status: "draft" } };
    } catch (err) {
      return { ok: false, error: { code: "SAVE_SKILL_FAILED", message: String(err?.message || err) } };
    }
  },
};

const skillTools = [list_skills, save_skill];

export default skillTools;
